import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { AnimatePresence, motion } from "framer-motion"
import * as Sentry from "@sentry/react"
import { useTranslation } from "react-i18next"
import { VList, VListHandle } from "virtua"
import { isEqual, throttle } from "lodash"
import useDebounce from "../hooks/useDebounce"
import SearchBar from "./SearchBar"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"

import { getCMRJid, getCarIssuesJid, getSlickshiftAIJid } from "@/jidUtils"
import useSlickAISilencePredicate from "@/hooks/useSlickAISilencePredicate"
import { usePrivateAgentTarget } from "@/hooks/usePrivateAgentTarget"
import { changeActiveChat, Chat, ChatsState } from "@/reducers/chatsSlice"
import {
  selectChatCounts,
  selectChats,
  selectChatsSettings,
} from "@/reducers/chatsSliceSelectors"
import { XmppContext } from "@/stream/xmppClient"
import UnreadNotificationPill from "./UnreadNotificationPill"
import { shallowEqual } from "react-redux"
import { sortChats } from "@/hooks/useSortedChatsByLastEvent"
import { selectMessages } from "@/reducers/messagesSliceSelectors"
import ChatCardContainer from "./chatCard/ChatCardNew"
import {
  isInitSyncDone,
  selectSyncFailedRoomJids,
} from "@/reducers/roomSyncStatusSelectors"
import { MessagesState } from "reducers/messagesSlice"
import ChatListGroupHeader from "@/components/ui/chat-list-group-header"
import {
  selectAccountChats,
  selectAccountChatsAsList,
} from "@/reducers/accountChatsSlice"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { selectArchivedChats } from "@/reducers/chatsSettingsSelectors"
import { addBookmark } from "@/api/xeps/PepNativeBookmarks"
import {
  joinChat,
  leavePeekedChats,
  peekChat,
} from "@/reducers/chatsSliceThunks"
import { TAB_TYPES, TabType } from "@/reducers/tabsSlice"
import {
  hideArchive,
  isArchiveVisible,
  selectChatListMode,
  selectLastActiveChatJid,
  showArchive,
} from "@/reducers/overlaysSlice"
import {
  selectDispatcherChats,
  selectDispatcherChatsStatus,
} from "@/reducers/dispatcherChatsSlice"
import { fetchDispatcherChats } from "@/reducers/dispatcherChatsThunks"
import { officeProfiles } from "@/reducers/profilesSliceSelectors"
import {
  buildDispatcherSections,
  DispatcherSection,
  UNASSIGNED_SECTION_JID,
} from "@/components/chatList/dispatcherSections"
import ForeignChatRow from "@/components/chatList/ForeignChatRow"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { useForeignChatActivity } from "@/hooks/useForeignChatActivity"
import { foreignActivityDenied } from "@/reducers/foreignChatActivitySlice"
import { removeFromFailed } from "@/reducers/roomSyncStatusSlice"
import { SidebarMenuButton } from "@/components/ui/sidebar-menu-button"
import { BellOff, Files, Sparkle } from "lucide-react"
import FleetOverview from "@/components/fleetOverview/FleetOverview"
import { createPortal } from "react-dom"
import { isMobile } from "@/utils/detectDevice"
import { mobileRoutes, setMobileRoute } from "@/routes/routes"
import { GlobeIcon } from "@/components/icons/GlobeIcon"
import { selectUserChats } from "@/reducers/userChatsSlice"
import {
  findTranschatUserChat,
  getChatDisplayName,
  isSlickshiftUser,
  shouldHideTranschatChat,
} from "@/lib/privateAgentUtils"

const ESTIMATED_ITEM_SIZE = 84 // Height of each chat item in pixels
const GROUP_HEADER_HEIGHT = 44 // Height of group headers

type ChatListProps = {
  onActiveChatChange: (chatJid: string, tab?: TabType) => void
  isArchive?: boolean
}

export type ChatType = string

type ChatWithMetadata = {
  chats: EnhancedChat[]
  isCollapsed: boolean
  unreadCount: number
}

export type EnhancedChat = Chat & {
  isArchived?: boolean
  isJoinable?: boolean // chat is in accountChats but not in chats
}

/** All a click needs to know about a chat. Another dispatcher's chat is only a
 * JID and a name here — everything else about it lives in the owner's client,
 * so it must not be invented to satisfy `EnhancedChat`. */
export type ChatClickTarget = {
  jid: string
  name: string
  isArchived?: boolean
  isJoinable?: boolean
}

type ChatsWithMetadata = Record<ChatType, ChatWithMetadata>

const HEADER_ITEM_TYPE = "header" as const
const CHAT_ITEM_TYPE = "chat" as const
const MENU_ITEM_TYPE = "menu" as const
const SEARCH_ITEM_TYPE = "search" as const
const SEPARATOR_ITEM_TYPE = "separator" as const
// A dispatcher header keys its collapsed state by JID, not by group name —
// two dispatchers can share a display name.
const DISPATCHER_HEADER_ITEM_TYPE = "dispatcher-header" as const
// A foreign chat is not a `Chat`, so ChatCardContainer has nothing to render.
const FOREIGN_CHAT_ITEM_TYPE = "foreign-chat" as const

type ItemType =
  | typeof HEADER_ITEM_TYPE
  | typeof CHAT_ITEM_TYPE
  | typeof MENU_ITEM_TYPE
  | typeof SEARCH_ITEM_TYPE
  | typeof SEPARATOR_ITEM_TYPE
  | typeof DISPATCHER_HEADER_ITEM_TYPE
  | typeof FOREIGN_CHAT_ITEM_TYPE

export type Item = {
  type: ItemType
  name?: ChatType
  count?: number
  data?: EnhancedChat
  groupName?: ChatType
  menuType?: "slick-ai" | "cmr" | "fleet-overview"
  dispatcherJid?: string
  foreignChat?: { jid: string; name: string }
  /** React key, when the item's own identity is not unique in the list. The
   * same chat can sit under several dispatchers, so a chat JID alone clashes. */
  key?: string
}

type CollapsedGroups = Set<ChatType>

// Define a type guard for "header" item type
function isHeader(item: Item): item is Item & {
  type: typeof HEADER_ITEM_TYPE
  name: string
  count: number
} {
  return (
    item?.type === HEADER_ITEM_TYPE &&
    item?.name !== undefined &&
    item?.count !== undefined
  )
}

// Define a type guard for "chat" item type
function isChat(item: Item): item is Item & {
  type: typeof CHAT_ITEM_TYPE
  data: EnhancedChat
  groupName: ChatType
} {
  return (
    item.type === CHAT_ITEM_TYPE &&
    item.data !== undefined &&
    item.groupName !== undefined
  )
}

// Define a type guard for "menu" item type
function isMenu(item: Item): item is Item & {
  type: typeof MENU_ITEM_TYPE
  menuType: "slick-ai" | "cmr" | "fleet-overview"
} {
  return item.type === MENU_ITEM_TYPE && item.menuType !== undefined
}

// Define a type guard for "search" item type
function isSearch(item: Item): item is Item & {
  type: typeof SEARCH_ITEM_TYPE
} {
  return item.type === SEARCH_ITEM_TYPE
}

// Define a type guard for "separator" item type
function isSeparator(item: Item): item is Item & {
  type: typeof SEPARATOR_ITEM_TYPE
} {
  return item.type === SEPARATOR_ITEM_TYPE
}

// Define a type guard for "dispatcher-header" item type
function isDispatcherHeader(item: Item): item is Item & {
  type: typeof DISPATCHER_HEADER_ITEM_TYPE
  name: string
  dispatcherJid: string
} {
  return (
    item.type === DISPATCHER_HEADER_ITEM_TYPE &&
    item.name !== undefined &&
    item.dispatcherJid !== undefined
  )
}

// Define a type guard for "foreign-chat" item type
function isForeignChat(item: Item): item is Item & {
  type: typeof FOREIGN_CHAT_ITEM_TYPE
  foreignChat: { jid: string; name: string }
} {
  return item.type === FOREIGN_CHAT_ITEM_TYPE && item.foreignChat !== undefined
}

// Return flat list of all items headers and chats for virtualization
function getFlatItemsList(params: {
  filterValue: string
  chatsWithMetadata: ChatsWithMetadata
  isArchive: boolean
  sortedChats: Chat[]
  showSlickAIMenu: boolean
  fleetOverviewEnabled: boolean
  hasInitSyncCompleted: boolean
  isMobile: boolean
}): Item[] {
  const {
    filterValue,
    chatsWithMetadata,
    isArchive,
    sortedChats,
    showSlickAIMenu,
    fleetOverviewEnabled,
    isMobile: isMobileDevice,
  } = params
  const filtered = (chats: EnhancedChat[]) => {
    if (!filterValue) return chats
    return chats.filter((chat) =>
      chat.name.toLowerCase().includes(filterValue.toLowerCase()),
    )
  }

  const flatItemList = []

  // Add menu items at the top (only on mobile and when not in archive)
  if (!isArchive && isMobileDevice) {
    // Add sidebar menu buttons
    if (showSlickAIMenu) {
      flatItemList.push({
        type: MENU_ITEM_TYPE,
        menuType: "slick-ai" as const,
      })
    }

    flatItemList.push({
      type: MENU_ITEM_TYPE,
      menuType: "cmr" as const,
    })

    if (fleetOverviewEnabled) {
      flatItemList.push({
        type: MENU_ITEM_TYPE,
        menuType: "fleet-overview" as const,
      })
    }

    // Add search bar if there are enough chats
    if (sortedChats?.length > 5) {
      flatItemList.push({
        type: SEARCH_ITEM_TYPE,
      })
    }

    // Add separator after menu items
    flatItemList.push({
      type: SEPARATOR_ITEM_TYPE,
    })
  }

  for (const [chatType, chatWithMetadata] of Object.entries(
    chatsWithMetadata,
  ) as [ChatType, ChatWithMetadata][]) {
    const filteredGroup = filtered(chatWithMetadata.chats)
    if (filteredGroup.length > 0) {
      flatItemList.push({
        type: HEADER_ITEM_TYPE,
        name: chatType,
        count: chatWithMetadata.unreadCount,
      })
      if (!chatWithMetadata.isCollapsed! || filterValue) {
        flatItemList.push(
          ...filteredGroup.map((chat) => ({
            type: CHAT_ITEM_TYPE,
            data: chat,
            groupName: chatType,
          })),
        )
      }
    }
  }

  return flatItemList
}

// Return flat list of dispatcher headers and their chats for virtualization
export function getDispatcherItemsList(params: {
  sections: DispatcherSection[]
  /** Sections the user has opened. A section nobody asked for is closed, so a
   * section seen for the first time never renders its rooms. */
  expandedDispatchers: Set<string>
  filterValue: string
  chatsByJid: Record<string, EnhancedChat>
  /** Translated name of the "no dispatcher" section; the stored one is Polish. */
  unassignedLabel?: string
}): Item[] {
  const { sections, expandedDispatchers, filterValue, chatsByJid } = params
  const needle = filterValue.toLowerCase()
  const items: Item[] = []

  for (const section of sections) {
    const chats = needle
      ? section.chats.filter((chat) => chat.name.toLowerCase().includes(needle))
      : section.chats
    if (chats.length === 0) continue

    items.push({
      type: DISPATCHER_HEADER_ITEM_TYPE,
      name: `${
        section.dispatcherJid === UNASSIGNED_SECTION_JID &&
        params.unassignedLabel
          ? params.unassignedLabel
          : section.label
      } (${chats.length})`,
      dispatcherJid: section.dispatcherJid,
      key: `dispatcher-${section.dispatcherJid}`,
    })

    // While filtering every section opens, same as the plain list does.
    if (!expandedDispatchers.has(section.dispatcherJid) && !filterValue)
      continue

    for (const chat of chats) {
      const own = chatsByJid[chat.jid]
      // Two dispatchers can keep the same chat, so the section has to be part
      // of the key — a chat JID alone would repeat inside one list.
      const key = `dispatcher-${section.dispatcherJid}-chat-${chat.jid}`
      if (chat.mine && own) {
        items.push({
          type: CHAT_ITEM_TYPE,
          data: own,
          groupName: section.dispatcherJid,
          key,
        })
      } else {
        items.push({
          type: FOREIGN_CHAT_ITEM_TYPE,
          foreignChat: { jid: chat.jid, name: chat.name },
          key,
        })
      }
    }
  }

  return items
}

const VirtualizedChatList = ({
  onActiveChatChange,
  isArchive = false,
}: ChatListProps) => {
  const { t } = useTranslation()
  const { myJid, client } = useContext(XmppContext)

  // Throttle functions for state changes
  const throttledChats = throttle((state: ChatsState) => state, 300)
  const throttledMessages = throttle((state: MessagesState) => state, 300)

  // Selectors with throttling applied
  const chatsFromState = useAppSelector(selectChats)
  const messagesFromState = useAppSelector(selectMessages)

  const archivedChats = useAppSelector(selectArchivedChats)
  const archiveVisible = useAppSelector(isArchiveVisible)

  // Slickshift AI chat

  const privateAgentEnabled = Boolean(useFeatureFlagEnabled("private-agent"))
  // A manager hands vehicles out; a dispatcher on a managed account no longer
  // joins chats on their own, so the joinable rows go.
  const { membershipLocked, isFleetManager } = useUserPermissions()

  // Using useMemo to compute throttled values based on selectors
  const chats = useMemo(() => {
    const chatData = throttledChats(chatsFromState)

    return chatData.filter((chat) =>
      isArchive
        ? archivedChats.includes(chat.jid)
        : !archivedChats.includes(chat.jid),
    )
  }, [chatsFromState, throttledChats, isArchive, archivedChats])

  const messages = useMemo(
    () => throttledMessages(messagesFromState),
    [messagesFromState, throttledMessages],
  )

  const [filterValue, setFilterValue] = useState("")
  const accountChats = useAppSelector(selectAccountChats)
  const accountChatsList = useAppSelector(selectAccountChatsAsList)
  const userChats = useAppSelector(selectUserChats)
  const isSlickshiftLoggedUser = isSlickshiftUser(myJid.local)
  const isSlickAISilenced = useSlickAISilencePredicate()
  // Seed with the already-sorted order so the list is correct on the first
  // paint (e.g. when returning to the Chats tab) instead of flashing the
  // unsorted order until the debounced effect below runs.
  const [sortedChats, setSortedChats] = useState(() =>
    sortChats(chats, messages, isSlickAISilenced),
  )
  const [fleetOverviewOpen, setFleetOverviewOpen] = useState(false)
  const fleetOverviewEnabled = Boolean(useFeatureFlagEnabled("fleet-overview"))
  const hasInitSyncCompleted = useAppSelector(isInitSyncDone)

  const onShowFleetOverview = useCallback(() => {
    setFleetOverviewOpen(true)
    if (isMobile) {
      setMobileRoute(mobileRoutes.FLEET_MAP)
    }
  }, [])

  // Track collapsed state for each group
  const [collapsedGroups, setCollapsedGroups] = useState<CollapsedGroups>(
    new Set(["Groups"]),
  )

  // Dispatcher sections keep their own set, keyed by JID, so switching modes
  // never rewrites which chat-type groups the user had collapsed. The set holds
  // the *expanded* ones: a section is closed until the user opens it, which is
  // what keeps a foreign room's archive unread until then. All sections start
  // collapsed.
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(
    () => new Set<string>(),
  )

  const activeChatJid = useMemo(
    () => chats.find((chat) => chat.active)?.jid || null,
    [chats],
  )

  // Debounce sorting chats as during init sync it could happen very often
  useDebounce(
    () => {
      const newSortedChats = sortChats(chats, messages, isSlickAISilenced)
      if (!isEqual(sortedChats, newSortedChats)) {
        setSortedChats(newSortedChats)
      }
    },
    [chats, messages, isSlickAISilenced],
    300,
  )

  const chatCounts = useAppSelector(
    selectChatCounts(myJid.toString()),
    shallowEqual,
  )

  const chatsSettings = useAppSelector(selectChatsSettings)
  const assistantChatJid = getSlickshiftAIJid(myJid)

  const getDisplayName = useCallback(
    (chatName: string, chatJid: string) =>
      getChatDisplayName({
        chatName,
        chatJid,
        isSlickshiftUser: isSlickshiftLoggedUser,
      }),
    [isSlickshiftLoggedUser],
  )

  const joinableAccountChatsList = useMemo(
    () =>
      membershipLocked
        ? []
        : accountChatsList
            .filter(
              (accountChat) =>
                accountChat.jid !== assistantChatJid &&
                (privateAgentEnabled || !userChats[accountChat.jid]),
            )
            .map((accountChat) => ({
              ...accountChat,
              name: getDisplayName(accountChat.name, accountChat.jid),
            })),
    [
      accountChatsList,
      assistantChatJid,
      privateAgentEnabled,
      userChats,
      getDisplayName,
      membershipLocked,
    ],
  )

  const transchatUserChat = useMemo(
    () => findTranschatUserChat(userChats, myJid.local),
    [userChats, myJid.local],
  )
  const ownTranschatJid = transchatUserChat?.jid

  const openSlickAITab = useCallback(
    (chatJid: string) => onActiveChatChange(chatJid, TAB_TYPES.SLICK_AI),
    [onActiveChatChange],
  )
  const { openPrivateAgentChat, targetJid: slickAITargetJid } =
    usePrivateAgentTarget(openSlickAITab)

  const shouldShowSlickAIMenu = Boolean(slickAITargetJid)

  const chatsWithMetadata: ChatsWithMetadata = useMemo(() => {
    const DEFAULT_GROUP_NAMES_ORDER = [
      "Groups",
      "Double Crew",
      "Drivers",
      "Shared Drivers",
      "Transics",
      "Whatsapp Contacts",
    ]

    let groupNames = Object.values(accountChats).map((chat) => chat.groupName)
    groupNames = [...new Set(DEFAULT_GROUP_NAMES_ORDER.concat(groupNames))]
    const grouped = groupNames.reduce((acc, groupName) => {
      // Get chats from current chats state (already joined)
      const existingChats: EnhancedChat[] = sortedChats
        .filter((c) => accountChats[c.jid]?.groupName === groupName)
        .map((chat) => ({
          ...chat,
          name: getDisplayName(chat.name, chat.jid),
          isArchived: archivedChats.includes(chat.jid),
          isJoinable: false,
        }))

      // When filtering, also include chats from accountChats that are not in current chats
      let additionalChats: EnhancedChat[] = []
      if (filterValue) {
        additionalChats = joinableAccountChatsList
          .filter(
            (accountChat) =>
              accountChat.groupName === groupName &&
              !accountChat.hidden &&
              !sortedChats.some((chat) => chat.jid === accountChat.jid) &&
              accountChat.name
                .toLowerCase()
                .includes(filterValue.toLowerCase()),
          )
          .map((accountChat) => ({
            jid: accountChat.jid,
            name: getDisplayName(accountChat.name, accountChat.jid),
            room: true, // AccountChats are typically group chats
            participants: [],
            active: false,
            draftMessage: "",
            groups: [],
            isArchived: archivedChats.includes(accountChat.jid),
            isJoinable: !archivedChats.includes(accountChat.jid),
          }))
      }

      const allChats = [...existingChats, ...additionalChats]

      const cmrJid = getCMRJid(myJid)
      const carIssuesJid = getCarIssuesJid(myJid)

      const filteredChats = allChats.filter((chat) => {
        if (chat.jid === cmrJid) return false
        if (chat.jid === carIssuesJid) return false
        if (chat.jid === assistantChatJid) return false
        if (
          shouldHideTranschatChat({
            privateAgentEnabled,
            isSlickshiftUser: isSlickshiftLoggedUser,
            chatJid: chat.jid,
            ownTranschatJid,
          })
        ) {
          return false
        }
        return true
      })

      const unreadCount = filteredChats.reduce(
        (acc, chat) =>
          acc +
          (chatsSettings[chat.jid]?.muted ? 0 : chatCounts[chat.jid] || 0),
        0,
      )

      if (filteredChats.length > 0) {
        acc[groupName] = {
          chats: filteredChats,
          unreadCount,
          isCollapsed: collapsedGroups.has(groupName),
        }
      }

      return acc
    }, {} as ChatsWithMetadata)

    return grouped
  }, [
    accountChats,
    sortedChats,
    filterValue,
    myJid,
    assistantChatJid,
    privateAgentEnabled,
    isSlickshiftLoggedUser,
    ownTranschatJid,
    archivedChats,
    joinableAccountChatsList,
    chatsSettings,
    chatCounts,
    collapsedGroups,
    getDisplayName,
  ])

  const chatListMode = useAppSelector(selectChatListMode)
  const byDispatcher = useAppSelector(selectDispatcherChats)
  const dispatcherProfiles = useAppSelector(officeProfiles)
  const inDispatcherMode = chatListMode === "dispatchers" && !isArchive

  const dispatcherNames = useMemo(
    () =>
      Object.fromEntries(
        dispatcherProfiles.map((profile) => [profile.jid, profile.name]),
      ),
    [dispatcherProfiles],
  )

  const dispatcherSections = useMemo(() => {
    if (!inDispatcherMode) return []
    return buildDispatcherSections({
      byDispatcher,
      accountChatsByJid: Object.fromEntries(
        Object.values(accountChats).map((chat) => [
          chat.jid,
          { name: chat.name, groupName: chat.groupName, hidden: chat.hidden },
        ]),
      ),
      myChatJids: chatsFromState.map((chat) => chat.jid),
      archivedChatJids: archivedChats,
      dispatcherNames,
      myJid: myJid.bare().toString(),
      // A manager sees the whole fleet, so the vehicles nobody keeps close the
      // list; a dispatcher with the flag only peeks at the other dispatchers.
      includeUnassigned: isFleetManager,
    })
  }, [
    inDispatcherMode,
    byDispatcher,
    accountChats,
    chatsFromState,
    archivedChats,
    dispatcherNames,
    myJid,
    isFleetManager,
  ])

  // Only the rooms of a section the user opened are asked for their last
  // message, so entering this view fetches nothing.
  // `filterValue` is deliberately ignored, unlike in the item builder above,
  // which opens every section while filtering: a keystroke must not fire a
  // query per room of the whole account, so searched rows show no time.
  const expandedForeignChatJids = useMemo(
    () =>
      dispatcherSections
        .filter((section) => expandedDispatchers.has(section.dispatcherJid))
        .flatMap((section) =>
          section.chats.filter((chat) => !chat.mine).map((chat) => chat.jid),
        ),
    [dispatcherSections, expandedDispatchers],
  )

  useForeignChatActivity(expandedForeignChatJids)

  // Ref for VList
  const vlistRef = useRef<VListHandle>(null)

  // Track scrolling state timeout for cleanup
  const isScrollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Track visible range for offscreen group calculations
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 })

  // Only the dispatcher list needs chats keyed by JID, so the plain list pays
  // nothing for it.
  const chatsByJid = useMemo(
    () =>
      inDispatcherMode
        ? Object.fromEntries(
            sortedChats.map((chat) => [
              chat.jid,
              {
                ...chat,
                name: getDisplayName(chat.name, chat.jid),
                isArchived: archivedChats.includes(chat.jid),
                isJoinable: false,
              } as EnhancedChat,
            ]),
          )
        : {},
    [inDispatcherMode, sortedChats, getDisplayName, archivedChats],
  )

  const items = useMemo(
    () =>
      inDispatcherMode
        ? getDispatcherItemsList({
            sections: dispatcherSections,
            expandedDispatchers,
            filterValue,
            chatsByJid,
            unassignedLabel: t("app.chatList.unassignedSection"),
          })
        : getFlatItemsList({
            filterValue,
            chatsWithMetadata,
            isArchive,
            sortedChats,
            showSlickAIMenu: shouldShowSlickAIMenu,
            fleetOverviewEnabled: fleetOverviewEnabled || false,
            hasInitSyncCompleted,
            isMobile: isMobile,
          }),
    [
      inDispatcherMode,
      dispatcherSections,
      expandedDispatchers,
      chatsByJid,
      filterValue,
      chatsWithMetadata,
      isArchive,
      sortedChats,
      shouldShowSlickAIMenu,
      fleetOverviewEnabled,
      hasInitSyncCompleted,
      t,
    ],
  )

  // The header renders "is it collapsed?", which for a dispatcher section is
  // simply "has nobody opened it?".
  const collapsedDispatchers = useMemo(
    () =>
      new Set(
        dispatcherSections
          .map((section) => section.dispatcherJid)
          .filter((dispatcherJid) => !expandedDispatchers.has(dispatcherJid)),
      ),
    [dispatcherSections, expandedDispatchers],
  )

  // Handle scroll state management
  const handleScroll = useCallback(() => {
    if (isScrollingTimeoutRef.current) {
      clearTimeout(isScrollingTimeoutRef.current)
    }

    isScrollingTimeoutRef.current = setTimeout(() => {
      // Update visible range for offscreen group calculations
      if (vlistRef.current) {
        const startIndex = vlistRef.current.findStartIndex()
        const endIndex = vlistRef.current.findEndIndex()
        setVisibleRange({ start: startIndex, end: endIndex })
      }
    }, 200)
  }, [])

  const scrollToGroup = useCallback((groupIndex: number) => {
    if (vlistRef.current) {
      vlistRef.current.scrollToIndex(groupIndex, {
        align: "center",
        smooth: true,
      })
    }
  }, [])

  type OffscreenGroupType = {
    nearestIndex: number
    totalCount: number
  }

  type OffscreenGroupsType = {
    top: OffscreenGroupType | null
    bottom: OffscreenGroupType | null
  }

  const [offscreenGroups, setOffscreenGroups] = useState<OffscreenGroupsType>({
    top: null,
    bottom: null,
  })

  const lastActiveChatJid = useAppSelector(selectLastActiveChatJid)

  const dispatch = useAppDispatch()

  // A manager lands in this view without touching the toggle (which is what
  // fetches on a switch), so the first render in it asks for the mapping.
  const dispatcherChatsStatus = useAppSelector(selectDispatcherChatsStatus)
  useEffect(() => {
    if (inDispatcherMode && dispatcherChatsStatus === "idle")
      dispatch(fetchDispatcherChats(client))
  }, [inDispatcherMode, dispatcherChatsStatus, dispatch, client])

  useEffect(() => {
    if (isArchive && archivedChats.length === 0 && archiveVisible) {
      dispatch(hideArchive())
      const fallbackJid = lastActiveChatJid || chatsFromState[0]?.jid
      if (fallbackJid) {
        dispatch(changeActiveChat({ activeChatJid: fallbackJid }))
      }
    }
  }, [
    archiveVisible,
    archivedChats,
    chatsFromState,
    dispatch,
    isArchive,
    lastActiveChatJid,
  ])

  const failedRooms = useAppSelector(selectSyncFailedRoomJids)

  // A peek into a room with no office account inside comes back `forbidden`.
  // Rather than leave a broken conversation open behind a lying connection bar,
  // the room goes back to the list carrying a quiet "no access".
  useEffect(() => {
    if (!inDispatcherMode) return
    const peeked = new Set(
      chatsFromState.filter((chat) => chat.peek).map((chat) => chat.jid),
    )
    const refused = failedRooms.filter((room) => peeked.has(room.jid))
    if (refused.length === 0) return

    refused.forEach((room) => {
      dispatch(foreignActivityDenied(room.jid))
      dispatch(removeFromFailed({ jid: room.jid }))
    })
    dispatch(leavePeekedChats(client))
    if (lastActiveChatJid) onActiveChatChange(lastActiveChatJid)
  }, [
    inDispatcherMode,
    failedRooms,
    chatsFromState,
    dispatch,
    client,
    lastActiveChatJid,
    onActiveChatChange,
  ])

  // Clean up peeked chats when leaving dispatcher mode. A ref tracks whether
  // we've been in the mode, so the effect doesn't fire on first mount.
  const wasInDispatcherMode = useRef(false)
  useEffect(() => {
    if (inDispatcherMode) {
      wasInDispatcherMode.current = true
    } else if (wasInDispatcherMode.current) {
      const activePeekedJid = chatsFromState.find(
        (chat) => chat.active && chat.peek,
      )?.jid
      dispatch(leavePeekedChats(client))
      // If the active chat was a peek, fall back to the last non-peek chat.
      if (activePeekedJid && lastActiveChatJid) {
        onActiveChatChange(lastActiveChatJid)
      }
      wasInDispatcherMode.current = false
    }
  }, [
    inDispatcherMode,
    dispatch,
    client,
    chatsFromState,
    lastActiveChatJid,
    onActiveChatChange,
  ])

  // Update offscreen groups when visible range updates
  useEffect(() => {
    if (!items.length) return

    const offscreenGroupsData: OffscreenGroupsType = { top: null, bottom: null }

    // Find first and last visible items
    const firstVisibleIndex = visibleRange.start
    const lastVisibleIndex = visibleRange.end

    // Check groups above viewport
    let totalCount = 0
    for (let i = 0; i < firstVisibleIndex; i++) {
      const item = items[i]
      // Use the type guard in your code
      if (isHeader(item)) {
        totalCount += item.count
        offscreenGroupsData.top = {
          nearestIndex: i,
          totalCount,
        }
      }
    }

    // Check groups below viewport
    totalCount = 0
    for (let i = items.length - 1; i > lastVisibleIndex; i--) {
      const item = items[i]
      if (isHeader(item)) {
        totalCount += item.count
        offscreenGroupsData.bottom = {
          nearestIndex: i,
          totalCount,
        }
      }
    }

    setOffscreenGroups(offscreenGroupsData)
  }, [visibleRange, items])

  const handleGroupHeaderToggle = useCallback(
    (groupType: ChatType) => {
      if (!filterValue) {
        setCollapsedGroups((prev) => {
          const newSet = new Set(prev)
          if (newSet.has(groupType)) {
            newSet.delete(groupType)
          } else {
            newSet.add(groupType)
          }
          return newSet
        })
      }
    },
    [filterValue],
  )

  const handleDispatcherHeaderToggle = useCallback(
    (dispatcherJid: string) => {
      if (!filterValue) {
        setExpandedDispatchers((prev) => {
          const newSet = new Set(prev)
          if (newSet.has(dispatcherJid)) {
            newSet.delete(dispatcherJid)
          } else {
            newSet.add(dispatcherJid)
          }
          return newSet
        })
      }
    },
    [filterValue],
  )

  const handleTopNotificationPillClick = useCallback(() => {
    if (offscreenGroups.top) {
      scrollToGroup(offscreenGroups.top.nearestIndex)
    }
  }, [offscreenGroups, scrollToGroup])

  const handleBottomNotificationPillClick = useCallback(() => {
    if (offscreenGroups.bottom) {
      scrollToGroup(offscreenGroups.bottom.nearestIndex)
    }
  }, [offscreenGroups, scrollToGroup])

  const handleChatClick = useCallback(
    async (chat: ChatClickTarget) => {
      // Drop peeked chats when navigating to any chat, keeping the target if it's
      // a peek transition.
      const isPeekTransition =
        inDispatcherMode && !chatsFromState.some((c) => c.jid === chat.jid)
      dispatch(
        leavePeekedChats(client, isPeekTransition ? chat.jid : undefined),
      )

      // Peek into a foreign chat: join without a bookmark.
      if (isPeekTransition) {
        await dispatch(peekChat(client, { jid: chat.jid, name: chat.name }))
        onActiveChatChange(chat.jid)
        return
      }

      if (chat.isArchived) {
        // Show archive and switch to this chat
        dispatch(showArchive({ lastActiveChatJid: activeChatJid || "" }))
        onActiveChatChange(chat.jid)
      } else if (chat.isJoinable) {
        // Join the chat like in JoinChatPopover
        const accountChat = joinableAccountChatsList.find(
          (ac) => ac.jid === chat.jid,
        )
        if (accountChat) {
          await addBookmark(client, accountChat.jid, accountChat.name)
          window.analytics.track("AddBookmark", {
            chatJid: accountChat?.jid,
            chatName: accountChat?.name,
            from: "ChatList",
          })
          dispatch(joinChat(client, accountChat))
          onActiveChatChange(accountChat.jid)
        }
      } else {
        // Regular chat click
        onActiveChatChange(chat.jid)
      }
    },
    [
      activeChatJid,
      dispatch,
      onActiveChatChange,
      joinableAccountChatsList,
      client,
      inDispatcherMode,
      chatsFromState,
    ],
  )

  const handleChatItemClick = useCallback(
    async (chat: ChatClickTarget) => {
      await handleChatClick(chat)
    },
    [handleChatClick],
  )

  // A row cannot await, so the peek is reported instead of being dropped.
  const handleForeignChatClick = useCallback(
    (chat: { jid: string; name: string }) => {
      handleChatClick(chat).catch((error) => Sentry.captureException(error))
    },
    [handleChatClick],
  )

  const handleSlickAIClick = useCallback(
    () => openPrivateAgentChat("ChatListSlickAIButton"),
    [openPrivateAgentChat],
  )

  const cmrChatJid = getCMRJid(myJid)

  const unreadCMR = chatCounts[cmrChatJid] || 0
  const isCMRMuted = Boolean(chatsSettings[cmrChatJid]?.muted)
  return (
    <>
      <div className="scrollbar-gutter-stable flex h-full flex-col">
        {/* Desktop menu items - only show when not mobile */}
        {!isMobile && !isArchive && (
          <MemoizedDesktopMenu
            sortedChats={sortedChats}
            filterValue={filterValue}
            setFilterValue={setFilterValue}
          />
        )}
        <div className="relative flex min-h-0 flex-1 flex-col scrollbar-borderless">
          {items.length > 0 &&
            offscreenGroups.top &&
            offscreenGroups.top?.totalCount > 0 && (
              <div className="absolute top-0 z-10 flex w-full justify-center py-2">
                <div
                  onClick={handleTopNotificationPillClick}
                  className="cursor-pointer"
                >
                  <UnreadNotificationPill
                    count={offscreenGroups.top.totalCount}
                    direction="top"
                  />
                </div>
              </div>
            )}

          {items.length > 0 ? (
            <VList
              ref={vlistRef}
              style={
                isMobile
                  ? { height: "calc(100dvh - 65px)", width: "100%" }
                  : { flex: 1, minHeight: 0, width: "100%" }
              }
              onScroll={handleScroll}
              overscan={10}
            >
              {items.map((item, index) => (
                <MemoizedVListItem
                  key={
                    item.key ??
                    (isHeader(item)
                      ? `header-${item.name}`
                      : isChat(item)
                        ? `chat-${item.data.jid}`
                        : isMenu(item)
                          ? `menu-${item.menuType}`
                          : isSearch(item)
                            ? `search-${index}`
                            : isSeparator(item)
                              ? `separator-${index}`
                              : `item-${index}`)
                  }
                  item={item}
                  index={index}
                  activeChatJid={activeChatJid}
                  isArchive={isArchive}
                  filterValue={filterValue}
                  collapsedGroups={
                    inDispatcherMode ? collapsedDispatchers : collapsedGroups
                  }
                  handleGroupHeaderToggle={
                    inDispatcherMode
                      ? handleDispatcherHeaderToggle
                      : handleGroupHeaderToggle
                  }
                  archiveVisible={archiveVisible}
                  onActiveChatChange={onActiveChatChange}
                  myJid={myJid}
                  isCMRMuted={isCMRMuted}
                  unreadCMR={unreadCMR}
                  onShowFleetOverview={onShowFleetOverview}
                  hasInitSyncCompleted={hasInitSyncCompleted}
                  setFilterValue={setFilterValue}
                  onChatClick={handleChatItemClick}
                  onForeignChatClick={handleForeignChatClick}
                  onSlickAIClick={handleSlickAIClick}
                />
              ))}
            </VList>
          ) : (
            <div className="p-5 text-center text-base font-medium">
              {t("app.chatList.noResults")}
            </div>
          )}
          {isMobile && items.length === 5 && (
            <div className="absolute left-1/2 top-[170px] -translate-x-1/2 transform p-5 text-center text-base font-medium">
              {t("app.chatList.noResults")}
            </div>
          )}
          {items.length > 0 &&
            offscreenGroups.bottom &&
            offscreenGroups.bottom.totalCount > 0 && (
              <div className="absolute bottom-0 z-10 flex w-full justify-center py-2">
                <div
                  onClick={handleBottomNotificationPillClick}
                  className="cursor-pointer"
                >
                  <UnreadNotificationPill
                    count={offscreenGroups.bottom.totalCount}
                    direction="bottom"
                  />
                </div>
              </div>
            )}
        </div>
      </div>
      {fleetOverviewOpen &&
        fleetOverviewEnabled &&
        createPortal(
          <FleetOverview
            onClose={() => setFleetOverviewOpen(false)}
            onNavigateToChat={onActiveChatChange}
          />,
          document.body,
        )}
    </>
  )
}

// Memoized menu button component to prevent unnecessary re-renders
const MemoizedMenuButton = memo(
  ({
    menuType,
    onActiveChatChange,
    myJid,
    onSlickAIClick,
    unreadCMR,
    isCMRMuted,
    onShowFleetOverview,
    hasInitSyncCompleted,
  }: {
    menuType: "slick-ai" | "cmr" | "fleet-overview"
    onActiveChatChange: (chatJid: string, tab?: TabType) => void
    myJid: any
    onSlickAIClick: () => Promise<void>
    unreadCMR: number
    isCMRMuted: boolean
    onShowFleetOverview: () => void
    hasInitSyncCompleted: boolean
  }) => {
    const { t } = useTranslation()
    const handleCMRClick = useCallback(() => {
      onActiveChatChange(getCMRJid(myJid), TAB_TYPES.CMR)
    }, [onActiveChatChange, myJid])

    return (
      <div className="mx-2 mb-1">
        {menuType === "slick-ai" && (
          <SidebarMenuButton
            onClick={() => void onSlickAIClick()}
            icon={Sparkle}
          >
            {t("app.chatList.askSlickAI")}
          </SidebarMenuButton>
        )}
        {menuType === "cmr" && (
          <SidebarMenuButton onClick={handleCMRClick} icon={Files}>
            <div className="flex items-center gap-2">
              {t("app.chatList.cmrDocuments")}
              {isCMRMuted && <BellOff className="h-4 w-4 text-gray-400" />}
              {unreadCMR !== 0 && (
                <div className="flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary p-1 font-semibold text-white">
                  {unreadCMR}
                </div>
              )}
            </div>
          </SidebarMenuButton>
        )}
        {menuType === "fleet-overview" && (
          <SidebarMenuButton
            onClick={onShowFleetOverview}
            icon={GlobeIcon}
            disabled={!hasInitSyncCompleted}
          >
            {t("app.chatList.fleetOverview")}
          </SidebarMenuButton>
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.menuType === nextProps.menuType &&
      prevProps.onSlickAIClick === nextProps.onSlickAIClick &&
      prevProps.unreadCMR === nextProps.unreadCMR &&
      prevProps.hasInitSyncCompleted === nextProps.hasInitSyncCompleted &&
      prevProps.onActiveChatChange === nextProps.onActiveChatChange &&
      prevProps.onShowFleetOverview === nextProps.onShowFleetOverview &&
      prevProps.myJid === nextProps.myJid
    )
  },
)

// Memoized desktop menu component to prevent unnecessary re-renders
const MemoizedDesktopMenu = memo(
  ({
    sortedChats,
    filterValue,
    setFilterValue,
  }: {
    sortedChats: Chat[]
    filterValue: string
    setFilterValue: (value: string) => void
  }) => {
    return (
      <AnimatePresence>
        <motion.div
          key="desktop-menu"
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {/* Desktop search bar */}
          {sortedChats?.length > 5 && (
            <div className="sticky top-0 z-10 flex max-h-[52px] flex-1 p-2">
              <SearchBar value={filterValue} onValueChange={setFilterValue} />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.sortedChats?.length === nextProps.sortedChats?.length &&
      prevProps.filterValue === nextProps.filterValue &&
      prevProps.setFilterValue === nextProps.setFilterValue
    )
  },
)

// Memoized chat item component to prevent unnecessary re-renders during virtualization
const MemoizedChatItem = memo(
  ({
    item,
    activeChatJid,
    isArchive,
    onChatClick,
  }: {
    item: Item & {
      type: typeof CHAT_ITEM_TYPE
      data: EnhancedChat
      groupName: ChatType
    }
    activeChatJid: string | null
    isArchive: boolean
    onChatClick: (chat: ChatClickTarget) => Promise<void>
  }) => (
    <ChatCardContainer
      key={item.data.jid}
      chatJid={item.data.jid}
      chatName={item.data.name}
      isActive={item.data.jid === activeChatJid}
      chatData={item.data}
      isArchive={isArchive}
      onClick={() => onChatClick(item.data)}
    />
  ),
  (prevProps, nextProps) => {
    // Custom comparison for the memoized chat item
    return (
      prevProps.item.data.jid === nextProps.item.data.jid &&
      prevProps.activeChatJid === nextProps.activeChatJid &&
      prevProps.isArchive === nextProps.isArchive &&
      prevProps.item.data === nextProps.item.data &&
      prevProps.onChatClick === nextProps.onChatClick
    )
  },
)

// Memoized VList item component to prevent unnecessary re-renders during virtualization
const MemoizedVListItem = memo(
  ({
    item,
    index,
    activeChatJid,
    isArchive,
    filterValue,
    collapsedGroups,
    handleGroupHeaderToggle,
    archiveVisible,
    onActiveChatChange,
    myJid,
    unreadCMR,
    isCMRMuted,
    onShowFleetOverview,
    hasInitSyncCompleted,
    setFilterValue,
    onChatClick,
    onForeignChatClick,
    onSlickAIClick,
  }: {
    item: Item
    index: number
    activeChatJid: string | null
    isArchive: boolean
    filterValue: string
    collapsedGroups: CollapsedGroups
    handleGroupHeaderToggle: (groupType: ChatType) => void
    archiveVisible: boolean
    onActiveChatChange: (chatJid: string, tab?: TabType) => void
    myJid: any
    unreadCMR: number
    isCMRMuted: boolean
    onShowFleetOverview: () => void
    hasInitSyncCompleted: boolean
    setFilterValue: (value: string) => void
    onChatClick: (chat: ChatClickTarget) => Promise<void>
    onForeignChatClick: (chat: { jid: string; name: string }) => void
    onSlickAIClick: () => Promise<void>
  }) => {
    const estimatedSize =
      item.type === HEADER_ITEM_TYPE ||
      item.type === DISPATCHER_HEADER_ITEM_TYPE
        ? GROUP_HEADER_HEIGHT
        : item.type === MENU_ITEM_TYPE
          ? 36
          : item.type === SEARCH_ITEM_TYPE
            ? 48
            : item.type === SEPARATOR_ITEM_TYPE
              ? 20
              : ESTIMATED_ITEM_SIZE

    return (
      <div data-index={index} style={{ minHeight: estimatedSize }}>
        {isHeader(item) ? (
          <ChatListGroupHeader
            name={item.name}
            count={filterValue ? 0 : item.count}
            isCollapsed={collapsedGroups.has(item.name)}
            onToggle={handleGroupHeaderToggle}
            filterValue={filterValue}
          />
        ) : isDispatcherHeader(item) ? (
          <ChatListGroupHeader
            name={item.name}
            count={0}
            isCollapsed={collapsedGroups.has(item.dispatcherJid)}
            // The header hands its own name to onToggle, but collapsing keys
            // by JID, so the JID is passed on instead.
            onToggle={() => handleGroupHeaderToggle(item.dispatcherJid)}
            filterValue={filterValue}
          />
        ) : isForeignChat(item) ? (
          <ForeignChatRow
            jid={item.foreignChat.jid}
            name={item.foreignChat.name}
            onClick={() => onForeignChatClick(item.foreignChat)}
          />
        ) : isMenu(item) && !archiveVisible ? (
          <MemoizedMenuButton
            menuType={item.menuType}
            onActiveChatChange={onActiveChatChange}
            myJid={myJid}
            onSlickAIClick={onSlickAIClick}
            isCMRMuted={isCMRMuted}
            unreadCMR={unreadCMR}
            onShowFleetOverview={onShowFleetOverview}
            hasInitSyncCompleted={hasInitSyncCompleted}
          />
        ) : isSearch(item) ? (
          <div className="sticky top-0 z-10 flex flex-1 p-2">
            <SearchBar value={filterValue} onValueChange={setFilterValue} />
          </div>
        ) : isSeparator(item) ? (
          <hr className="mx-3 mb-3 mt-1" />
        ) : (
          isChat(item) && (
            <MemoizedChatItem
              item={item}
              activeChatJid={activeChatJid}
              isArchive={isArchive}
              onChatClick={onChatClick}
            />
          )
        )}
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if the item or key props actually changed
    if (prevProps.item !== nextProps.item) return false
    if (prevProps.index !== nextProps.index) return false

    // For chat items, check if active state changed
    if (isChat(prevProps.item) && isChat(nextProps.item)) {
      return (
        prevProps.activeChatJid === nextProps.activeChatJid &&
        prevProps.isArchive === nextProps.isArchive &&
        prevProps.onChatClick === nextProps.onChatClick
      )
    }

    // For header items, check if collapsed state or filter changed
    if (isHeader(prevProps.item) && isHeader(nextProps.item)) {
      return (
        prevProps.filterValue === nextProps.filterValue &&
        prevProps.collapsedGroups === nextProps.collapsedGroups &&
        prevProps.handleGroupHeaderToggle === nextProps.handleGroupHeaderToggle
      )
    }

    // For dispatcher headers, the same props matter as for group headers
    if (
      isDispatcherHeader(prevProps.item) &&
      isDispatcherHeader(nextProps.item)
    ) {
      return (
        prevProps.filterValue === nextProps.filterValue &&
        prevProps.collapsedGroups === nextProps.collapsedGroups &&
        prevProps.handleGroupHeaderToggle === nextProps.handleGroupHeaderToggle
      )
    }

    // For foreign chats, only the chat behind the row and its click matter —
    // the last-message time comes from the store, inside the row itself.
    if (isForeignChat(prevProps.item) && isForeignChat(nextProps.item)) {
      return (
        prevProps.item.foreignChat.jid === nextProps.item.foreignChat.jid &&
        prevProps.onForeignChatClick === nextProps.onForeignChatClick
      )
    }

    // For menu items, check relevant props
    if (isMenu(prevProps.item) && isMenu(nextProps.item)) {
      return (
        prevProps.archiveVisible === nextProps.archiveVisible &&
        prevProps.unreadCMR === nextProps.unreadCMR &&
        prevProps.isCMRMuted === nextProps.isCMRMuted &&
        prevProps.hasInitSyncCompleted === nextProps.hasInitSyncCompleted &&
        prevProps.onSlickAIClick === nextProps.onSlickAIClick &&
        prevProps.onActiveChatChange === nextProps.onActiveChatChange &&
        prevProps.onShowFleetOverview === nextProps.onShowFleetOverview
      )
    }

    // For search items, check filter props
    if (isSearch(prevProps.item) && isSearch(nextProps.item)) {
      return (
        prevProps.filterValue === nextProps.filterValue &&
        prevProps.setFilterValue === nextProps.setFilterValue
      )
    }

    // For separator items, always equal
    return !!(isSeparator(prevProps.item) && isSeparator(nextProps.item))
  },
)

MemoizedMenuButton.displayName = "MemoizedMenuButton"

export default memo(Sentry.withProfiler(VirtualizedChatList))
